/**
 * Diff the plan against the current intervals.icu calendar for DISPLAY — decides
 * which plan sessions are already on the calendar unchanged ("kept") vs new, and
 * which in-window events the plan no longer wants ("removed"). This is the same
 * reconciliation publishPlan.reconcile() performs at publish time, by the same
 * workout signature (date + name + sport + description), so the calendar's
 * colours match exactly what publishing will do. Kept as a separate, pure,
 * tested function so the two can't silently drift.
 */

export interface DiffSession {
  date: string;          // "YYYY-MM-DD"
  name: string;
  sport: string;
  description: string;
}

export interface DiffEvent {
  id: number;
  date: string;          // "YYYY-MM-DD"
  name: string;
  type: string;          // sport
  description: string;
}

export interface PlanCalendarDiff {
  /** Per plan session (input order): true if an existing event already matches it. */
  sessionKept: boolean[];
  /** In-window events with no matching plan session — deleted on publish. */
  removedEventIds: Set<number>;
}

function normalizeDescription(description: string): string {
  return (description ?? "").replace(/\r\n/g, "\n").trim();
}

export function sessionSignature(s: DiffSession): string {
  return [s.date, s.name, s.sport, normalizeDescription(s.description)].join(" ");
}

export function eventSignature(e: DiffEvent): string {
  return [e.date, e.name ?? "", e.type ?? "", normalizeDescription(e.description ?? "")].join(" ");
}

export function diffPlanVsCalendar(sessions: DiffSession[], windowEvents: DiffEvent[]): PlanCalendarDiff {
  // Index existing events by signature so each session can claim one match.
  const bySignature = new Map<string, number[]>();
  for (const e of windowEvents) {
    const sig = eventSignature(e);
    const bucket = bySignature.get(sig);
    if (bucket) bucket.push(e.id);
    else bySignature.set(sig, [e.id]);
  }

  const kept = new Set<number>();
  const sessionKept = sessions.map(s => {
    const bucket = bySignature.get(sessionSignature(s));
    if (bucket && bucket.length > 0) {
      kept.add(bucket.shift()!);   // consume one matching event
      return true;
    }
    return false;
  });

  const removedEventIds = new Set<number>();
  for (const e of windowEvents) if (!kept.has(e.id)) removedEventIds.add(e.id);

  return { sessionKept, removedEventIds };
}
